import React, { useEffect, useState } from 'react';
import { Alert, Box, Card, Grid, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { ScheduleStep } from './steps/ScheduleStep';
import { Form, FormikProps } from 'formik';
import { ScheduledEventInput } from 'src/features/reporting/store/types';
import WO2Button from 'src/common/components/button/Button';
import { EmailTemplateStep } from './steps/EmailTemplateStep';
import { useHistory } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { flattenArray } from 'src/common';
import { Props } from '../container';
import { ClientFilterStep } from './steps/ClientFilterStep';
import { ReportParameterStep } from './steps/ReportParameterStep';

interface ReportingSchedulerStepperProps extends Props {
  initialStep: number;
  formikProps: FormikProps<ScheduledEventInput>;
  editing: boolean;
  handleSubmit: (values: ScheduledEventInput, trigger: boolean) => void;
  discardChanges?: (formikProps: FormikProps<ScheduledEventInput>) => void;
}

export const ReportingSchedulerStepper = (props: ReportingSchedulerStepperProps): JSX.Element => {
  const [activeStep, setActiveStep] = useState<number>(props.initialStep);

  const history = useHistory();

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  useEffect(() => {
    const errors = props.formikProps.errors;
    if (props.formikProps.isSubmitting) {
      if (
        errors.name ||
        errors.recurrenceType ||
        errors.occurrences ||
        errors.startOn ||
        errors.endOn ||
        errors.recurrenceRule
      ) {
        setActiveStep(0);
      } else if (errors.clientSearchFilter) {
        setActiveStep(1);
      } else if (errors.reportParameters) {
        setActiveStep(2);
      } else if (errors.emailParameters) {
        setActiveStep(3);
      }
    }
  }, [props.formikProps.errors, props.formikProps.isSubmitting]);

  return (
    <Form>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <WO2Button variant="outlined" onClick={() => history.replace('/reporting?tab=scheduler')}>
            Report Scheduler
          </WO2Button>
        </Grid>
        <Grid item>
          <Card elevation={3} sx={{ padding: 2 }}>
            <Box display="flex" flexDirection="column" gap={4} mt={2} mb={2}>
              <Stepper activeStep={activeStep}>
                <Step>
                  <StepLabel>Schedule & Frequency</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Client Filtering</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Report Parameters</StepLabel>
                </Step>
                {props.formikProps.values.emailParameters && (
                  <Step>
                    <StepLabel>Email Template</StepLabel>
                  </Step>
                )}
              </Stepper>

              {Object.entries(props.formikProps.errors).length > 0 && (
                <Alert variant="outlined" severity="error">
                  <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                    {Object.entries(flattenArray(props.formikProps.errors)).map(([key, error]) => (
                      <li key={key}>
                        <Typography variant="body2">{error}</Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              {activeStep === 0 ? (
                <ScheduleStep {...props} />
              ) : activeStep === 1 ? (
                <ClientFilterStep {...props} />
              ) : activeStep == 2 ? (
                <ReportParameterStep {...props} />
              ) : (
                <EmailTemplateStep {...props} />
              )}
            </Box>
          </Card>
        </Grid>
        <Grid item container spacing={2}>
          {activeStep > 0 && (
            <Grid item>
              <WO2Button onClick={handleBack} variant="contained">
                Back
              </WO2Button>
            </Grid>
          )}
          {props.editing && (
            <Grid item>
              <WO2Button
                onClick={() => {
                  if (props.discardChanges) {
                    props.discardChanges(props.formikProps);
                  }
                }}
                variant="outlined"
              >
                Discard Changes
              </WO2Button>
            </Grid>
          )}

          <Grid item flex={1} />

          {activeStep >= (props.formikProps.values.emailParameters ? 3 : 2) && (
            <>
              <Grid item>
                <LoadingButton
                  loading={
                    props.editing
                      ? props.updateScheduledEventProgress.isLoading
                      : props.createScheduledEventProgress.isLoading
                  }
                  variant="contained"
                  onClick={() => props.handleSubmit(props.formikProps.values, false)}
                >
                  Save
                </LoadingButton>
              </Grid>
              <Grid item>
                <LoadingButton
                  loading={
                    props.updateScheduledEventProgress.isLoading || props.triggerScheduledEventProgress.isLoading
                  }
                  variant="contained"
                  onClick={() => props.handleSubmit(props.formikProps.values, true)}
                >
                  Save & Run
                </LoadingButton>
              </Grid>
            </>
          )}

          {activeStep < (props.formikProps.values.emailParameters ? 3 : 2) && (
            <Grid item>
              <WO2Button variant="contained" onClick={handleNext}>
                Next
              </WO2Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Form>
  );
};
